import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useOpenAddSong } from '../../../../smartLinks/catalog/addSong/useOpenAddSong';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { StudioActionError } from '../../studio/StudioActionError';
import { useProfileMake } from '../useProfileMake';
import { profileMessages as m } from '../profileMessages';
import { ReleasePickerDialog } from './ReleasePickerDialog';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileEmptyStateProps {
  accountId: number;
  profile: ArtistProfileResponse;
}

/**
 * The first visit (design a2, d4): "Make your profile set from <newest release>" makes the Spotify
 * header and the two banners from its blurred cover in one click. With no song: "Add a song first".
 */
const ProfileEmptyStateComponent: React.FC<ProfileEmptyStateProps> = ({ accountId, profile }) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const make = useProfileMake(accountId);
  const openAddSong = useOpenAddSong('studio', accountId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const release = profile.newestRelease;

  if (release === null) {
    return (
      <div className={styles.useIt} data-testid="profile-empty-no-song">
        <div className={styles.grow}>
          <b>{intl.formatMessage(m.emptyNoSongTitle)}</b>
          <small>{intl.formatMessage(m.emptyNoSongBody)}</small>
        </div>
        <SMLButton variant="primary" onClick={openAddSong}>
          {intl.formatMessage(m.emptyAddSong)}
        </SMLButton>
      </div>
    );
  }

  const makeSet = () => {
    make.fromRelease(release.catalogItemUuid).catch(console.error);
  };

  return (
    <div className="d-flex flex-column gap-2">
      <div className={styles.useIt} data-testid="profile-empty">
        <div className={styles.grow}>
          <b>{intl.formatMessage(m.emptyTitle, { title: release.title })}</b>
          <small>{intl.formatMessage(m.emptyBody)}</small>
        </div>
        <SMLButton variant="primary" disabled={make.isBusy} onClick={makeSet}>
          {intl.formatMessage(narrow ? m.emptyMakeNarrow : m.emptyMake)}
        </SMLButton>
        {!narrow && <SMLButton onClick={() => setPickerOpen(true)}>{intl.formatMessage(m.emptyPickAnother)}</SMLButton>}
      </div>
      <StudioActionError feedback={make.feedback} />
      <ReleasePickerDialog
        accountId={accountId}
        isOpen={pickerOpen}
        initialUuid={release.catalogItemUuid}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
};

export const ProfileEmptyState = TypedMemo(ProfileEmptyStateComponent);
